"use client";

import { Copy, Check, ArrowRight } from "lucide-react";
import { useState } from "react";

import { Badge } from "@/lib/components/badge";
import { Button } from "@/lib/components/button";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/lib/components/tooltip";
import { cn } from "@/lib/utils";

import { PRICING_CONFIG } from "@routerbench/shared";
import { getProviderIcon } from "@routerbench/shared/components";

import { ModelCodeExampleDialog } from "./model-code-example-dialog";

import type {
	ModelDefinition,
	ProviderModelMapping,
	providers,
	StabilityLevel,
} from "@routerbench/models";

interface ModelWithProviders extends ModelDefinition {
	providerDetails: Array<{
		provider: ProviderModelMapping;
		providerInfo: (typeof providers)[number];
	}>;
}

export function ModelCard({
	model,
	getCapabilityIcons,
	goToModel,
	currency = "usd",
}: {
	model: ModelWithProviders;
	getCapabilityIcons: (
		provider: ProviderModelMapping,
		model?: any,
	) => {
		icon: any;
		label: string;
		color: string;
	}[];
	shouldShowStabilityWarning?: (
		stability?: StabilityLevel,
	) => boolean | undefined;
	goToModel: () => void;
	formatPrice?: any;
	currency?: "inr" | "usd";
}) {
	const [copied, setCopied] = useState(false);

	const copyToClipboard = (text: string, e: React.MouseEvent) => {
		e.stopPropagation();
		navigator.clipboard.writeText(text);
		setCopied(true);
		setTimeout(() => setCopied(false), 2000);
	};

	const mainProvider = model.providerDetails[0];

	// Get min prices across all providers
	const currencySymbol = currency === "usd" ? "$" : "$";
	const rate = currency === "usd" ? 1 : PRICING_CONFIG.usdToInrRate;

	const getMinPrice = (type: "input" | "output") => {
		const prices = model.providerDetails
			.map((p) => {
				const price =
					type === "input" ? p.provider.inputPrice : p.provider.outputPrice;
				if (!price) {
					return null;
				}
				return (
					price *
					1e6 *
					rate *
					(p.provider.discount ? 1 - p.provider.discount : 1)
				);
			})
			.filter((p) => p !== null) as number[];

		return prices.length > 0 ? Math.min(...prices) : null;
	};

	const minInputPrice = getMinPrice("input");
	const minOutputPrice = getMinPrice("output");

	// Video models are priced per second of output rather than per token.
	const videoPerSecond = model.providerDetails.flatMap((p) =>
		p.provider.videoPricingTiers && p.provider.videoPricingTiers.length > 0
			? p.provider.videoPricingTiers.map((t) => t.pricePerSecond)
			: p.provider.videoPricePerSecond
				? [p.provider.videoPricePerSecond]
				: [],
	);
	const minVideoPrice =
		videoPerSecond.length > 0 ? Math.min(...videoPerSecond) * rate : null;

	// Image-generation models are priced per generated image (requestPrice).
	const imagePerImage = model.providerDetails.flatMap((p) =>
		p.provider.imageGenerations && p.provider.requestPrice
			? [
					p.provider.requestPrice *
						(p.provider.discount ? 1 - p.provider.discount : 1),
				]
			: [],
	);
	const minImagePrice =
		imagePerImage.length > 0 ? Math.min(...imagePerImage) * rate : null;

	// Audio models: TTS priced per character (shown per 1M), STT per second (shown per minute).
	const ttsChars = model.providerDetails.flatMap((p) =>
		p.provider.characterPrice
			? [
					p.provider.characterPrice *
						(p.provider.discount ? 1 - p.provider.discount : 1),
				]
			: [],
	);
	const minTtsPrice =
		ttsChars.length > 0 ? Math.min(...ttsChars) * 1e6 * rate : null;
	const sttSecs = model.providerDetails.flatMap((p) =>
		p.provider.secondPrice
			? [
					p.provider.secondPrice *
						(p.provider.discount ? 1 - p.provider.discount : 1),
				]
			: [],
	);
	const minSttPrice =
		sttSecs.length > 0 ? Math.min(...sttSecs) * 60 * rate : null;

	const getStabilityBadge = () => {
		if (!model.stability || model.stability === "stable") {
			return null;
		}

		const badges = {
			beta: {
				label: "Beta",
				className: "bg-blue-500/10 text-blue-600 border-blue-500/20",
			},
			unstable: {
				label: "Unstable",
				className: "bg-orange-500/10 text-orange-600 border-orange-500/20",
			},
			experimental: {
				label: "Experimental",
				className: "bg-purple-500/10 text-purple-600 border-purple-500/20",
			},
		};

		const badge = badges[model.stability as keyof typeof badges];
		return badge ? (
			<Badge
				variant="outline"
				className={cn("text-xs font-medium", badge.className)}
			>
				{badge.label}
			</Badge>
		) : null;
	};

	const capabilities = getCapabilityIcons(mainProvider.provider, model);
	const maxContext = Math.max(
		...model.providerDetails.map((p) => p.provider.contextSize || 0),
	);

	return (
		<div
			onClick={goToModel}
			className="group relative flex flex-col h-full rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-black hover:border-gray-300 dark:hover:border-gray-700 transition-all duration-200 cursor-pointer overflow-hidden"
		>
			{/* Hover effect gradient */}
			<div className="absolute inset-0 bg-gradient-to-br from-gray-50/50 to-transparent dark:from-gray-900/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none" />

			<div className="relative flex flex-col h-full p-6">
				{/* Header */}
				<div className="flex items-start justify-between gap-3 mb-4">
					<div className="flex-1 min-w-0">
						<h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1 truncate">
							{model.name || model.id}
						</h3>
						<p className="text-sm text-gray-500 dark:text-gray-400 uppercase tracking-wide font-medium">
							{model.family}
						</p>
					</div>
					<div className="flex items-center gap-2">
						{getStabilityBadge()}
						{model.free && (
							<Badge
								variant="outline"
								className="text-xs font-medium bg-status-ok-bg text-status-ok-title border-status-ok-border"
							>
								Free
							</Badge>
						)}
					</div>
				</div>

				{/* Model ID with copy and code button */}
				<div className="mb-4">
					<div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50">
						<code className="text-xs font-mono text-gray-600 dark:text-gray-400 flex-1 truncate">
							{model.id}
						</code>
						<div
							onClick={(e) => e.stopPropagation()}
							onMouseDown={(e) => e.stopPropagation()}
							className="flex items-center gap-1"
						>
							<ModelCodeExampleDialog modelId={model.id} />
							<Button
								variant="ghost"
								size="sm"
								className="h-6 w-6 p-0"
								onClick={(e) => copyToClipboard(model.id, e)}
							>
								{copied ? (
									<Check className="h-3.5 w-3.5 text-green-600" />
								) : (
									<Copy className="h-3.5 w-3.5 text-gray-500" />
								)}
							</Button>
						</div>
					</div>
				</div>

				{/* Stats Grid */}
				<div className="grid grid-cols-2 gap-3 mb-4">
					{/* Context Size */}
					<div className="flex flex-col">
						<span className="text-xs text-gray-500 dark:text-gray-400 mb-1">
							Context
						</span>
						<span className="text-sm font-semibold text-gray-900 dark:text-white">
							{maxContext.toLocaleString()} tokens
						</span>
					</div>

					{/* Providers */}
					<div className="flex flex-col">
						<span className="text-xs text-gray-500 dark:text-gray-400 mb-1">
							Providers
						</span>
						<span className="text-sm font-semibold text-gray-900 dark:text-white">
							{model.providerDetails.length}
						</span>
					</div>

					{/* Input Price */}
					{minInputPrice !== null && (
						<div className="flex flex-col">
							<span className="text-xs text-gray-500 dark:text-gray-400 mb-1">
								Input
							</span>
							<span className="text-sm font-semibold text-gray-900 dark:text-white">
								{currencySymbol}
								{minInputPrice.toFixed(2)}/M
							</span>
						</div>
					)}

					{/* Output Price */}
					{minOutputPrice !== null && (
						<div className="flex flex-col">
							<span className="text-xs text-gray-500 dark:text-gray-400 mb-1">
								Output
							</span>
							<span className="text-sm font-semibold text-gray-900 dark:text-white">
								{currencySymbol}
								{minOutputPrice.toFixed(2)}/M
							</span>
						</div>
					)}

					{/* Video Price (per second) */}
					{minVideoPrice !== null && (
						<div className="flex flex-col">
							<span className="text-xs text-gray-500 dark:text-gray-400 mb-1">
								Video
							</span>
							<span className="text-sm font-semibold text-gray-900 dark:text-white">
								{currencySymbol}
								{minVideoPrice.toFixed(2)}/s
							</span>
						</div>
					)}

					{/* Image Price (per generated image) */}
					{minImagePrice !== null && (
						<div className="flex flex-col">
							<span className="text-xs text-gray-500 dark:text-gray-400 mb-1">
								Image
							</span>
							<span className="text-sm font-semibold text-gray-900 dark:text-white">
								{currencySymbol}
								{minImagePrice.toFixed(3)}/img
							</span>
						</div>
					)}

					{/* Speech (TTS) price per 1M characters */}
					{minTtsPrice !== null && (
						<div className="flex flex-col">
							<span className="text-xs text-gray-500 dark:text-gray-400 mb-1">
								Speech
							</span>
							<span className="text-sm font-semibold text-gray-900 dark:text-white">
								{currencySymbol}
								{minTtsPrice.toFixed(2)}/M
							</span>
						</div>
					)}

					{/* Transcription (STT) price per minute */}
					{minSttPrice !== null && (
						<div className="flex flex-col">
							<span className="text-xs text-gray-500 dark:text-gray-400 mb-1">
								Audio
							</span>
							<span className="text-sm font-semibold text-gray-900 dark:text-white">
								{currencySymbol}
								{minSttPrice.toFixed(3)}/min
							</span>
						</div>
					)}
				</div>

				{/* Capabilities */}
				{capabilities.length > 0 && (
					<div className="mb-4">
						<div className="flex flex-wrap gap-2">
							{capabilities.slice(0, 6).map(({ icon: Icon, label }) => (
								<Tooltip key={label}>
									<TooltipTrigger asChild>
										<div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50 hover:border-gray-300 dark:hover:border-gray-700 transition-colors">
											<Icon className="h-3.5 w-3.5 text-gray-600 dark:text-gray-400 flex-shrink-0" />
											<span className="text-xs font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap">
												{label}
											</span>
										</div>
									</TooltipTrigger>
									<TooltipContent>
										<p className="text-xs">{label}</p>
									</TooltipContent>
								</Tooltip>
							))}
						</div>
					</div>
				)}

				{/* Providers List */}
				<div className="mt-auto">
					<div className="flex flex-wrap gap-2">
						{model.providerDetails
							.slice(0, 4)
							.map(({ provider, providerInfo }) => {
								const ProviderIcon = getProviderIcon(provider.providerId);
								return (
									<Tooltip key={provider.providerId}>
										<TooltipTrigger asChild>
											<div className="flex items-center justify-center w-8 h-8 rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-black">
												{ProviderIcon ? (
													<ProviderIcon className="h-4 w-4" />
												) : (
													<span className="text-xs font-semibold">
														{(providerInfo?.name || provider.providerId)
															.charAt(0)
															.toUpperCase()}
													</span>
												)}
											</div>
										</TooltipTrigger>
										<TooltipContent>
											<p className="text-xs">
												{providerInfo?.name || provider.providerId}
											</p>
										</TooltipContent>
									</Tooltip>
								);
							})}
						{model.providerDetails.length > 4 && (
							<div className="flex items-center justify-center w-8 h-8 rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50 text-xs font-semibold text-gray-600 dark:text-gray-400">
								+{model.providerDetails.length - 4}
							</div>
						)}
					</div>
				</div>

				{/* Hover Arrow */}
				<div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
					<ArrowRight className="h-4 w-4 text-gray-400" />
				</div>
			</div>
		</div>
	);
}
